import { useCallback, useEffect, useMemo, useState } from "react";
import { AssetService } from "@/services/asset-service";
import type { AssetChart } from "@/schemas";
import { Loader2, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { CHART_ANIMATION } from "@/lib/chart-animation";
import { getCurrencySymbol } from "@/lib/formatters";
import {
  computeAssetPriceChange,
  filterChartToLatestIntradaySession,
  getValidatedPreviousClose,
} from "@/components/asset/price-change";
import type { DayChangeAsset } from "@/components/asset/price-change";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

interface PriceChartProps {
  assetId: string;
  currency: string;
  previousClose?: number;
  previousCloseAt?: string;
  formatPrice: (price: number, currency: string) => string;
  resolveCurrency: (id?: string) => string;
  onPeriodData?: (data: { period: string; priceChange: number; currentPrice: number | null }) => void;
}

const PERIODS = [
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "1y", label: "1Y" },
  { value: "max", label: "Max" },
];

export function PriceChart({
  assetId,
  currency,
  previousClose,
  previousCloseAt,
  formatPrice,
  resolveCurrency,
  onPeriodData,
}: PriceChartProps) {
  const [period, setPeriod] = useState("1m");
  const [chartData, setChartData] = useState<AssetChart | null>(null);
  const [dailyReferenceChartData, setDailyReferenceChartData] = useState<AssetChart | null>(null);
  const [loading, setLoading] = useState(false);

  const loadChart = useCallback(async () => {
    try {
      setLoading(true);
      const data = await AssetService.getChart(assetId, period);
      setChartData(data);
    } catch (error) {
      console.error(error);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [assetId, period]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  useEffect(() => {
    if (period !== "1d") {
      return;
    }

    const loadDailyReferenceChart = async () => {
      try {
        const data = await AssetService.getChart(assetId, "1y");
        setDailyReferenceChartData(data);
      } catch (error) {
        console.error(error);
        setDailyReferenceChartData(null);
      }
    };

    loadDailyReferenceChart();
  }, [assetId, period]);

  const dayChangeAsset: DayChangeAsset = {
    previous_close: previousClose,
    previous_close_at: previousCloseAt,
  };

  const displayedChartData = useMemo(
    () => (period === "1d" ? filterChartToLatestIntradaySession(chartData) : chartData),
    [chartData, period]
  );

  const priceChange = useMemo(() => {
    const currentPrice = displayedChartData?.price_points?.length
      ? displayedChartData.price_points[displayedChartData.price_points.length - 1].price
      : null;

    return computeAssetPriceChange(
      dayChangeAsset,
      period,
      currentPrice,
      displayedChartData,
      dailyReferenceChartData
    );
  }, [displayedChartData, dailyReferenceChartData, dayChangeAsset, period]);

  const previousClosePrice = useMemo(
    () => getValidatedPreviousClose(dayChangeAsset, dailyReferenceChartData),
    [dailyReferenceChartData, dayChangeAsset]
  );

  // Notify parent of period data changes
  useEffect(() => {
    if (onPeriodData) {
      const currentPrice = displayedChartData?.price_points?.length
        ? displayedChartData.price_points[displayedChartData.price_points.length - 1].price
        : null;
      onPeriodData({ period, priceChange, currentPrice });
    }
  }, [period, priceChange, displayedChartData, onPeriodData]);

  const chartColor = priceChange >= 0 ? "#22c55e" : "#ef4444";
  const yAxisDomain = useMemo(() => {
    if (!displayedChartData?.price_points?.length) {
      return ["auto", "auto"] as [string, string];
    }

    const prices = displayedChartData.price_points.map((point) => point.price);
    if (period === "1d" && previousClosePrice && previousClosePrice > 0) {
      prices.push(previousClosePrice);
    }

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1 || max * 0.05;
    return [min - padding, max + padding] as [number, number];
  }, [displayedChartData, period, previousClosePrice]);

  return (
    <div className="relative">
      {/* Period Selector */}
      <div className="absolute top-0 left-0 z-10 flex items-center gap-2">
        <div className="flex items-center gap-1 bg-background/80 backdrop-blur-md rounded-lg p-1 border shadow-sm">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={cn(
                "px-2 py-1 text-[10px] font-bold rounded-md transition-all",
                period === p.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-[360px] pt-10">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
          </div>
        ) : displayedChartData && displayedChartData.price_points.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={displayedChartData.price_points}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e5e7eb"
                strokeOpacity={0.5}
              />
              <XAxis
                dataKey="timestamp"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  if (period === "1d") {
                    return date.toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                  }
                  return date.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  });
                }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                domain={yAxisDomain}
                orientation="right"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                tickFormatter={(val) => {
                  const symbol = getCurrencySymbol(resolveCurrency(currency));
                  return `${symbol}${val.toLocaleString()}`;
                }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-background/95 backdrop-blur-md border border-border rounded-xl p-3 shadow-2xl text-xs font-mono">
                        <p className="text-muted-foreground mb-1">
                          {new Date(
                            payload[0].payload.timestamp
                          ).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                        <p className="text-lg font-bold text-primary">
                          {formatPrice(
                            payload[0].value as number,
                            resolveCurrency(currency)
                          )}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {period === "1d" &&
                previousClosePrice &&
                previousClosePrice > 0 && (
                  <ReferenceLine
                    y={previousClosePrice}
                    stroke="#94a3b8"
                    strokeWidth={1.25}
                    strokeDasharray="4 5"
                    strokeOpacity={0.75}
                    ifOverflow="extendDomain"
                    label={{
                      value: `Prev close ${formatPrice(previousClosePrice, resolveCurrency(currency))}`,
                      position: "top",
                      fill: "#94a3b8",
                      fontSize: 10,
                    }}
                  />
                )}
              <Line
                type="monotone"
                dataKey="price"
                stroke={chartColor}
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  strokeWidth: 0,
                  fill: chartColor,
                }}
                {...CHART_ANIMATION}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground bg-muted/5 rounded-xl border-2 border-dashed border-muted">
            <TrendingUp className="h-12 w-12 mb-3 opacity-10" />
            <p className="text-sm font-medium">
              No historical data available
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
